import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Grid,
  Switch,
} from '@mui/material';
import {
  MoveToInbox as MoveIcon,
  Warehouse as WarehouseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useReclassifyCore } from '../../../../core/modules/reclassify';
import { ArticleForReclassify } from '../../../../core/modules/reclassify/interfaces/get-articles-for-reclassify';
import { ReclassifyBulkRequest } from '../../../../core/modules/reclassify/interfaces/post-reclassify';
import { DestinationTypeSelector, DestinationType } from '../../shared/DestinationTypeSelector';
import { useMasterCore } from '../../../../core/modules/master';
import { useCompanyCore } from '../../../../core/modules/company';
import { useWarehouseCore } from '../../../../core/modules/warehouse';

interface ReclassifyDialogProps {
  open: boolean;
  articles: ArticleForReclassify[];
  onClose: () => void;
  onSuccess?: () => void;
}

export const ReclassifyDialog: React.FC<ReclassifyDialogProps> = ({
  open,
  articles,
  onClose,
  onSuccess,
}) => {
  const {
    containers,
    locations,
    productStates,
    reclassifyLoading,
    lastReclassifyResult,
    loadContainers,
    loadLocations,
    reclassifyArticles,
    loadArticles,
  } = useReclassifyCore();

  const { companyId } = useCompanyCore();
  const { warehouses, getWarehouses } = useWarehouseCore();
  const { 
    productUsages,
    productCompletenesses,
    productFuntionalities,
    productPackagings,
    productStates: masterProductStates,
    productAesthetics,
    getProductAttributes,
    channelTypes,
    getTypes
  } = useMasterCore();

  const [destinationType, setDestinationType] =
    useState<DestinationType>('container');
  const [selectedContainer, setSelectedContainer] = useState<number | null>(
    null
  );
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [quantitiesToMove, setQuantitiesToMove] = useState<Record<number, number>>({});
  const [observations, setObservations] = useState<Record<number, string>>({});
  const [articleStatuses, setArticleStatuses] = useState<Record<number, number>>({});
  
  // Estados para clasificación detallada de cada artículo
  const [articleClassificationData, setArticleClassificationData] = useState<Record<number, {
    usage_id?: number;
    usage_observation?: string;
    packaging_id?: number;
    packaging_observation?: string;
    functionality_id?: number;
    functionality_observation?: string;
    aesthetic_id?: number;
    aesthetic_observation?: string;
    completeness_id?: number;
    completeness_observation?: string;
    state_id?: number;
    reference?: string;
  }>>({});
  
  // Estados para el modal de edición detallada
  const [detailedEditDialogOpen, setDetailedEditDialogOpen] = useState(false);
  const [selectedArticleForDetailedEdit, setSelectedArticleForDetailedEdit] = useState<ArticleForReclassify | null>(null);
  const [detailedEditLoading, setDetailedEditLoading] = useState(false);
  const [detailedEditError, setDetailedEditError] = useState<string | null>(null);
  const [detailedForm, setDetailedForm] = useState({
    usage_id: 0,
    usage_observation: '',
    packaging_id: 0,
    packaging_observation: '',
    functionality_id: 0,
    functionality_observation: '',
    aesthetic_id: 0,
    aesthetic_observation: '',
    completeness_id: 0,
    completeness_observation: '',
    state_id: 0,
    reference: '',
    observation: ''
  });

  useEffect(() => {
    if (open && companyId) {
      // Cargar datos disponibles cuando se abre el diálogo
      loadContainers();
      loadLocations();
      getWarehouses(companyId);
      getTypes(companyId, 'channel');

      // Reset form
      setDestinationType('container');
      setSelectedContainer(null);
      setSelectedLocation(null);
      
      // Inicializar cantidades con el total disponible
      const initialQuantities: Record<number, number> = {};
      const initialObservations: Record<number, string> = {};
      const initialStatuses: Record<number, number> = {};
      articles.forEach(article => {
        initialQuantities[article.id] = article.quantity || 0;
        initialObservations[article.id] = article.observation || '';
        initialStatuses[article.id] = article.status_id || 0;
      });
      setQuantitiesToMove(initialQuantities);
      setObservations(initialObservations);
      setArticleStatuses(initialStatuses);
    }
  }, [open, companyId, loadContainers, loadLocations, getWarehouses, getTypes, articles]);

  // Cargar opciones de clasificación cuando se monta el componente
  useEffect(() => {
    if (companyId) {
      getProductAttributes(companyId, 'usage');
      getProductAttributes(companyId, 'packaging');
      getProductAttributes(companyId, 'functionality');
      getProductAttributes(companyId, 'aesthetic');
      getProductAttributes(companyId, 'completeness');
      getProductAttributes(companyId, 'state');
    }
  }, [companyId, getProductAttributes]);

  const handleSubmit = async () => {
    if (!articles.length) return;

    const request: ReclassifyBulkRequest = {
      items: articles.map((article) => {
        const classificationData = articleClassificationData[article.id] || {};
        return {
          article_id: article.id,
          quantity: quantitiesToMove[article.id] || article.quantity || 0,
          status_id: articleStatuses[article.id] || article.status_id,
          observation: observations[article.id] || '',
          to_container_id:
            destinationType === 'container'
              ? selectedContainer || undefined
              : undefined,
          to_location_id:
            destinationType === 'location'
              ? selectedLocation || undefined
              : undefined,
          // Campos de clasificación detallada
          usage_id: classificationData.usage_id,
          usage_observation: classificationData.usage_observation,
          packaging_id: classificationData.packaging_id,
          packaging_observation: classificationData.packaging_observation,
          functionality_id: classificationData.functionality_id,
          functionality_observation: classificationData.functionality_observation,
          aesthetic_id: classificationData.aesthetic_id,
          aesthetic_observation: classificationData.aesthetic_observation,
          completeness_id: classificationData.completeness_id,
          completeness_observation: classificationData.completeness_observation,
          state_id: classificationData.state_id,
          reference: classificationData.reference,
        };
      }),
    };

    try {
      await reclassifyArticles(
        request,
        () => {
          // Success callback
          loadArticles(); // Refrescar lista de artículos
          onSuccess?.();
          onClose();
        },
        () => {
          // Failure callback - error will be shown in UI
        }
      );
    } catch (error) {
      // Error is already handled in the core hook
    }
  };

  const handleQuantityChange = (articleId: number, quantity: number) => {
    setQuantitiesToMove(prev => ({
      ...prev,
      [articleId]: Math.max(0, Math.min(quantity, articles.find(a => a.id === articleId)?.quantity || 0))
    }));
  };

  const handleObservationChange = (articleId: number, observation: string) => {
    setObservations(prev => ({
      ...prev,
      [articleId]: observation
    }));
  };

  const handleStatusChange = (articleId: number, statusId: number) => {
    setArticleStatuses(prev => ({
      ...prev,
      [articleId]: statusId
    }));
  };

  const handleDetailedEditClick = (article: ArticleForReclassify) => {
    setSelectedArticleForDetailedEdit(article);
    setDetailedEditError(null);
    
    // Inicializar formulario con datos existentes del artículo
    const formData = {
      usage_id: article.usage_id || 0,
      usage_observation: article.usage_observation || '',
      packaging_id: article.packaging_id || 0,
      packaging_observation: article.packaging_observation || '',
      functionality_id: article.functionality_id || 0,
      functionality_observation: article.functionality_observation || '',
      aesthetic_id: article.aesthetic_id || 0,
      aesthetic_observation: article.aesthetic_observation || '',
      completeness_id: article.completeness_id || 0,
      completeness_observation: article.completeness_observation || '',
      state_id: article.state_id || article.status_id || 0,
      reference: article.reference || '',
      observation: article.observation || ''
    };
    
    setDetailedForm(formData);
    setDetailedEditDialogOpen(true);
  };

  const handleSaveDetailedEdit = () => {
    if (!selectedArticleForDetailedEdit) return;
    
    // Guardar los datos de clasificación detallada para este artículo
    setArticleClassificationData(prev => ({
      ...prev,
      [selectedArticleForDetailedEdit.id]: {
        usage_id: detailedForm.usage_id || undefined,
        usage_observation: detailedForm.usage_observation || undefined,
        packaging_id: detailedForm.packaging_id || undefined,
        packaging_observation: detailedForm.packaging_observation || undefined,
        functionality_id: detailedForm.functionality_id || undefined,
        functionality_observation: detailedForm.functionality_observation || undefined,
        aesthetic_id: detailedForm.aesthetic_id || undefined,
        aesthetic_observation: detailedForm.aesthetic_observation || undefined,
        completeness_id: detailedForm.completeness_id || undefined,
        completeness_observation: detailedForm.completeness_observation || undefined,
        state_id: detailedForm.state_id || undefined,
        reference: detailedForm.reference || undefined,
      }
    }));
    
    setDetailedEditDialogOpen(false);
  };

  const isValid = () => {
    if (destinationType === 'container') {
      return selectedContainer !== null;
    } else {
      return selectedLocation !== null;
    }
  };

  const getDestinationLabel = () => {
    if (destinationType === 'container' && selectedContainer) {
      const container = containers?.find((c) => c.id === selectedContainer);
      return container ? `${container.code} - ${container.description}` : '';
    } else if (destinationType === 'location' && selectedLocation) {
      const location = locations?.find((l) => l.id === selectedLocation);
      return location ? `${location.code} - ${location.description}` : '';
    }
    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 700,
          maxHeight: '90vh',
          width: '90vw',
          maxWidth: '1200px'
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MoveIcon color="primary" />
          <Typography variant="h6">
            Reclasificar artículos ({articles.length})
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Tabla de artículos a reclasificar */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Artículos a reclasificar:
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Ubicación Actual</TableCell>
                    <TableCell>Cantidad Actual</TableCell>
                    <TableCell>Cantidad a Mover</TableCell>
                    <TableCell>Observación</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articles.map((article) => (
                    <TableRow key={article.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={article.files?.[0]?.URL}
                            alt={article.name || article.description}
                            sx={{ width: 32, height: 32 }}
                          >
                            {(article.name || article.description).charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {article.name || article.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {article.code || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={articleStatuses[article.id] || article.status_id || ''}
                            displayEmpty
                            onChange={(e: SelectChangeEvent<number>) => {
                              const statusId = parseInt(e.target.value as string) || 0;
                              handleStatusChange(article.id, statusId);
                            }}
                          >
                            <MenuItem value="">Sin estado</MenuItem>
                            {productStates.map((state) => (
                              <MenuItem key={state.id} value={state.id}>
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {locations?.find((l) => l.id === article.location_id)?.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {article.quantity || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={quantitiesToMove[article.id] || article.quantity || 0}
                          onChange={(e) => handleQuantityChange(article.id, parseInt(e.target.value) || 0)}
                          inputProps={{ 
                            min: 0, 
                            max: article.quantity || 0,
                            style: { textAlign: 'center', width: '60px' }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Observación..."
                          value={observations[article.id] || ''}
                          onChange={(e) => handleObservationChange(article.id, e.target.value)}
                          multiline
                          rows={1}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="info"
                          onClick={() => handleDetailedEditClick(article)}
                          size="small"
                        >
                          <Tooltip title="Editar Detalles">
                            <EditIcon />
                          </Tooltip>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Selector de tipo de destino */}
          <DestinationTypeSelector
            destinationType={destinationType}
            onDestinationTypeChange={setDestinationType}
            selectedContainer={selectedContainer}
            onContainerChange={setSelectedContainer}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            containers={containers || []}
            locations={locations || []}
            loading={reclassifyLoading}
            warehouses={warehouses || []}
            channelTypes={channelTypes || []}
          />

          {/* Resumen */}
          {isValid() && (
            <Alert severity="info">
              <Typography variant="body2">
                Se reclasificarán <strong>{articles.length}</strong> artículo(s) 
                con un total de <strong>
                  {Object.values(quantitiesToMove).reduce((sum, qty) => sum + qty, 0)}
                </strong> unidades a:
                <br />
                <strong>{getDestinationLabel()}</strong>
              </Typography>
            </Alert>
          )}

          {/* Resultado de la última operación */}
          {lastReclassifyResult && (
            <Alert
              severity={lastReclassifyResult.success ? 'success' : 'warning'}
            >
              <Typography variant="body2">
                {lastReclassifyResult.message}
              </Typography>

              {lastReclassifyResult.errors &&
                lastReclassifyResult.errors.length > 0 && (
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      display="block"
                      fontWeight="medium"
                    >
                      Errores encontrados:
                    </Typography>
                    {lastReclassifyResult.errors.map((error, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        display="block"
                        color="error"
                      >
                        • Artículo ID {error.article_id}: {error.error_message}
                      </Typography>
                    ))}
                  </Box>
                )}

              {lastReclassifyResult.reclassified_items &&
                lastReclassifyResult.reclassified_items.length > 0 && (
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      display="block"
                      fontWeight="medium"
                      color="success.main"
                    >
                      Reclasificados exitosamente:{' '}
                      {lastReclassifyResult.reclassified_items.length}{' '}
                      artículo(s)
                    </Typography>
                  </Box>
                )}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={reclassifyLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid() || reclassifyLoading}
          startIcon={
            reclassifyLoading ? <CircularProgress size={20} /> : <MoveIcon />
          }
        >
          {reclassifyLoading ? 'Reclasificando...' : 'Reclasificar'}
        </Button>
      </DialogActions>

      {/* Modal de Edición Detallada */}
      <Dialog
        open={detailedEditDialogOpen}
        onClose={() => setDetailedEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h6">Editar Detalles del Artículo</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedArticleForDetailedEdit && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Información del artículo */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Artículo:
                </Typography>
                <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Avatar src={selectedArticleForDetailedEdit.files?.[0]?.URL} sx={{ width: 40, height: 40 }}>
                    {selectedArticleForDetailedEdit.description.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedArticleForDetailedEdit.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Código: {selectedArticleForDetailedEdit.code}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Error message */}
              {detailedEditError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {detailedEditError}
                  </Alert>
                </Grid>
              )}

              {/* Estados del Producto */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Estados del Producto
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Uso */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Uso *</InputLabel>
                      <Select
                        value={detailedForm.usage_id || ''}
                        label="Uso *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, usage_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(productUsages || []).map((usage: any) => (
                          <MenuItem key={usage.id} value={usage.id}>
                            {usage.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Uso"
                      value={detailedForm.usage_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, usage_observation: e.target.value }))}
                      placeholder="Observación sobre el uso..."
                    />
                  </Grid>

                  {/* Empaquetado */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Empaquetado *</InputLabel>
                      <Select
                        value={detailedForm.packaging_id || ''}
                        label="Empaquetado *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, packaging_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(productPackagings || []).map((packaging: any) => (
                          <MenuItem key={packaging.id} value={packaging.id}>
                            {packaging.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Empaquetado"
                      value={detailedForm.packaging_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, packaging_observation: e.target.value }))}
                      placeholder="Observación sobre el empaquetado..."
                    />
                  </Grid>

                  {/* Funcionalidad */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Funcionalidad *</InputLabel>
                      <Select
                        value={detailedForm.functionality_id || ''}
                        label="Funcionalidad *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, functionality_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(productFuntionalities || []).map((functionality: any) => (
                          <MenuItem key={functionality.id} value={functionality.id}>
                            {functionality.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Funcionalidad"
                      value={detailedForm.functionality_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, functionality_observation: e.target.value }))}
                      placeholder="Observación sobre la funcionalidad..."
                    />
                  </Grid>

                  {/* Estético */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estético *</InputLabel>
                      <Select
                        value={detailedForm.aesthetic_id || ''}
                        label="Estético *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, aesthetic_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(productAesthetics || []).map((aesthetic: any) => (
                          <MenuItem key={aesthetic.id} value={aesthetic.id}>
                            {aesthetic.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Estético"
                      value={detailedForm.aesthetic_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, aesthetic_observation: e.target.value }))}
                      placeholder="Observación sobre el estado estético..."
                    />
                  </Grid>

                  {/* Completitud */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Completitud *</InputLabel>
                      <Select
                        value={detailedForm.completeness_id || ''}
                        label="Completitud *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, completeness_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(productCompletenesses || []).map((completeness: any) => (
                          <MenuItem key={completeness.id} value={completeness.id}>
                            {completeness.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación Completitud"
                      value={detailedForm.completeness_observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, completeness_observation: e.target.value }))}
                      placeholder="Observación sobre la completitud..."
                    />
                  </Grid>

                  {/* Estado */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estado *</InputLabel>
                      <Select
                        value={detailedForm.state_id || ''}
                        label="Estado *"
                        onChange={(e) => setDetailedForm(prev => ({ ...prev, state_id: parseInt(e.target.value as string) || 0 }))}
                      >
                        {(masterProductStates || []).map((state: any) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Estado General */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Estado General
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referencia"
                      value={detailedForm.reference}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Referencia del producto..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observación General"
                      value={detailedForm.observation}
                      onChange={(e) => setDetailedForm(prev => ({ ...prev, observation: e.target.value }))}
                      placeholder="Observación general..."
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setDetailedEditDialogOpen(false)}
            disabled={detailedEditLoading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveDetailedEdit}
            variant="contained"
            disabled={detailedEditLoading}
            startIcon={detailedEditLoading ? <CircularProgress size={20} /> : <EditIcon />}
            sx={{ borderRadius: 2 }}
          >
            {detailedEditLoading ? 'Guardando...' : 'Guardar Detalles'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
